'use client';

import Image from 'next/image';
import Link from 'next/link';
import type { HTMLAttributes } from 'react';
import { trackEvent } from '@/utils/analytics';
import type { services } from '@/utils/constants';
import { cn } from '@/utils/utils';

interface ServiceCardProps extends HTMLAttributes<HTMLAnchorElement> {
  service: (typeof services)[number];
}

export default function ServiceCard({
  service,
  className,
  ...props
}: ServiceCardProps) {
  const handleServiceClick = () => {
    trackEvent({
      action: 'service_card_click',
      category: 'services',
      label: service.title,
      value: 1,
    });
  };

  return (
    <Link
      className={cn(
        'relative group h-[250px] sm:h-[300px] md:h-[350px] overflow-hidden rounded-4xl shadow-lg',
        className,
      )}
      href={service.href}
      aria-label={`Book ${service.title} service now`}
      onClick={handleServiceClick}
      {...props}
    >
      {/* Background Image */}
      <Image
        fill
        src={service.image}
        alt={`${service.title} service offered by Enlil Travel`}
        sizes="(max-width: 640px) 100vw, (max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
        className="object-cover transition-transform duration-500 group-hover:scale-110"
        priority={service.id <= 2} // Load first two images with priority
      />

      {/* Overlay gradient */}
      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/50 to-transparent pointer-events-none" />

      {/* Content */}
      <div className="absolute inset-0 flex flex-col justify-end p-4 sm:p-6 text-white">
        <div className="flex items-center space-x-1 mb-2">
          <span className="text-yellow-400">★</span>
          <span className="text-sm">{service.rating}</span>
        </div>
        <h2 className="text-xl sm:text-2xl font-semibold">{service.title}</h2>
        <p className="text-xs sm:text-sm text-gray-200 mt-2 line-clamp-2">
          {service.description}
        </p>

        {/* Button that appears on hover - visible by default on mobile */}
        <div
          className="mt-4 inline-block py-2 px-6 border text-white font-medium rounded-full text-center 
                transition-all duration-300 group-hover:translate-y-0 group-hover:bg-white group-hover:text-black"
        >
          <span>Book Now</span>
        </div>
      </div>
    </Link>
  );
}
